import React, { useState, useEffect } from 'react';
import Table from '../../../components/Table/Table';
import Button from '../../../components/Button/Button';
import Toggle from '../../../components/Toggle/Toggle';
import InputField from '../../../components/InputField/InputField';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { useToast } from '../../../contexts/ToastContext';
import styles from './FeatureFlagsPage.module.css';

const FeatureFlagsPage: React.FC = () => {
    const [showModal, setShowModal] = useState(false);
    const [newKey, setNewKey] = useState('');
    const [newName, setNewName] = useState('');
    const [newEnabled, setNewEnabled] = useState(false);
    const [newRollout, setNewRollout] = useState('100');

    const { loading, error, flags, fetchFlags, toggleFlag, createFlag } = useFeatureFlags();
    const { showToast } = useToast();

    useEffect(() => { fetchFlags().catch(() => {}); }, [fetchFlags]);

    const handleToggle = async (id: number, currentEnabled: boolean) => {
        try {
            await toggleFlag(id, !currentEnabled);
            showToast('Feature flag updated.', 'success');
        } catch {
            showToast('Failed to toggle feature flag.', 'error');
        }
    };

    const handleCreate = async () => {
        if (!newKey.trim() || !newName.trim()) return;
        try {
            await createFlag({
                key: newKey.trim(),
                name: newName.trim(),
                enabled: newEnabled,
                rolloutPercent: Number(newRollout) || 100
            });
            showToast('Feature flag created.', 'success');
            setShowModal(false);
            setNewKey('');
            setNewName('');
            setNewEnabled(false);
            setNewRollout('100');
        } catch {
            showToast('Failed to create feature flag.', 'error');
        }
    };

    const tableHeaders = ['Key', 'Name', 'Enabled', 'Rollout %'];
    const tableData = (flags ?? []).map((f, i) => {
        const enabled = Boolean(f.enabled);
        const rollout = typeof f.rolloutPercent === 'number' ? f.rolloutPercent : 0;
        return {
            originalIndex: i,
            'Key': f.key,
            'Name': f.name,
            'Enabled': (
                <Toggle checked={enabled} onChange={() => handleToggle(f.id, enabled)} />
            ),
            'Rollout %': `${rollout}%`
        };
    });

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerText}>
                    <h1 className={styles.title}>Feature Flags</h1>
                    <p className={styles.subtitle}>Control feature rollout across the platform</p>
                </div>
                <Button label="Create Flag" onClick={() => setShowModal(true)} />
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    {error}
                    <Button label="Retry" onClick={() => fetchFlags().catch(() => {})} size="small" />
                </div>
            )}

            <div className={styles.tableWrapper}>
                <Table
                    headers={tableHeaders}
                    data={tableData}
                    loading={loading}
                    nonSortableColumns={['Enabled']}
                    customRenderers={{ 'Enabled': (v) => v }}
                />
            </div>

            {(flags?.length ?? 0) === 0 && !loading && !error && (
                <div className={styles.emptyState}>
                    <h3>No feature flags</h3>
                    <p>Create your first feature flag to get started.</p>
                </div>
            )}

            {showModal && (
                <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>Create Feature Flag</h2>
                            <button className={styles.closeButton} onClick={() => setShowModal(false)}>&times;</button>
                        </div>
                        <div className={styles.modalContent}>
                            <div className={styles.field}>
                                <InputField label="Key" value={newKey} onChange={setNewKey} placeholder="e.g. enable_chat" required />
                            </div>
                            <div className={styles.field}>
                                <InputField label="Name" value={newName} onChange={setNewName} placeholder="e.g. Enable Chat Feature" required />
                            </div>
                            <div className={styles.field}>
                                <Toggle label="Enabled" checked={newEnabled} onChange={setNewEnabled} />
                            </div>
                            <div className={styles.field}>
                                <InputField label="Rollout %" value={newRollout} onChange={setNewRollout} type="number" placeholder="100" />
                            </div>
                        </div>
                        <div className={styles.modalActions}>
                            <Button label="Cancel" onClick={() => setShowModal(false)} variant="secondary" />
                            <Button label="Create" onClick={handleCreate} disabled={loading || !newKey.trim() || !newName.trim()} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FeatureFlagsPage;
